import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {
  FolderBreadcrumb,
  FolderBreadcrumbs,
  StyledAncestorFolderBreadcrumbStyleProps,
} from '../../components/folderBreadcrumbs/folderBreadcrumbs';

/*
 * Props.
 */

interface SettingsBreadcrumbsProps {
  breadcrumbs: ReadonlyArray<FolderBreadcrumb>;
  topLevelBreadcrumb: FolderBreadcrumb;
  showSeparatorAfterTopLevel?: boolean;
  separatorIsHighlighted?: boolean;
}

/*
 * Style.
 */

const commonBreadcrumbStyles = css`
  display: grid;
  grid-auto-flow: column;
  grid-column-gap: 3px;
  grid-auto-columns: auto;
  align-items: center;
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
  min-height: 16px;
`;

const StyledFolderBreadcrumbSpan = styled.span<StyledAncestorFolderBreadcrumbStyleProps>`
  min-width: fit-content;
  color: ${(p) => p.theme.greys.shade60};
  ${commonBreadcrumbStyles};

  &:hover {
    color: ${(p) => p.theme.greys.shade80};

    > div,
    > svg {
      opacity: 1;
    }
  }

  > div,
  > svg {
    opacity: ${(p) => (p.$isSolitaryTopLevelBreadcrumb ? '1' : '0.4')};
  }
`;

const StyledChevronIcon = styled(Icon)`
  display: inline-block;
  vertical-align: text-bottom;
  margin: 2px 2px 1px;
  color: ${(p) => p.theme.greys.shade60};
`;

/*
 * Component.
 */

export const SettingsBreadcrumbs: FC<SettingsBreadcrumbsProps> = (props) => {
  const theme = useUserTheme();

  return (
    <FolderBreadcrumbs
      styles={{
        ancestorSpan: StyledFolderBreadcrumbSpan,
        currentFolderSpan: StyledFolderBreadcrumbSpan,
        showSeparatorAfterTopLevel: props.showSeparatorAfterTopLevel ?? false,
        breadcrumbSeparator: (
          <StyledChevronIcon
            name="chevronRightTiny"
            color={props.separatorIsHighlighted ? theme.greys.shade80 : theme.greys.shade60}
          />
        ),
      }}
      topLevelBreadcrumb={props.topLevelBreadcrumb}
      folderPathBreadcrumbs={props.breadcrumbs}
    />
  );
};
